import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { PlaceAddress, PlaceSession, PlaceSuggestionsService } from './place-suggestions.service';

const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

export interface AddressSuggestionRow
{
    id: string;
    label: string;
    description?: string;
}

/** Google address suggestions for a `ph-remote-search` street field; build it in a field initializer. */
export class AddressSuggestions
{
    rows: AddressSuggestionRow[] = [];

    private readonly _places = inject(PlaceSuggestionsService);
    private readonly _queries = new Subject<string>();
    private _session: PlaceSession = this._places.newSession();
    private _latest = '';

    constructor(private readonly _changed: () => void) {
        this._queries.pipe(
                debounceTime(DEBOUNCE_MS),
                switchMap(query => query.length >= MIN_CHARS
                        ? this._places.suggest(query, this._session, ['address']).pipe(map(found => ({query, found})))
                        : of({query, found: []})),
                takeUntilDestroyed(inject(DestroyRef)),
        ).subscribe(({query, found}) => {
            if (query !== this._latest) {
                return;
            }
            this.rows = found.filter(place => !!place.placeId).map(place => place.secondaryText
                    ? {id: place.placeId, label: place.mainText, description: place.secondaryText}
                    : {id: place.placeId, label: place.mainText});
            this._changed();
        });
    }

    get status(): 'idle' | 'results' {
        return this.rows.length ? 'results' : 'idle';
    }

    search(text: string): void {
        this._latest = (text ?? '').trim();
        if (this._latest.length < MIN_CHARS) {
            this.clear();
        }
        this._queries.next(this._latest);
    }

    clear(): void {
        if (this.rows.length) {
            this.rows = [];
            this._changed();
        }
    }

    /** The full address behind a picked row; ends the billing session so the next lookup starts a new one. */
    pick(row: AddressSuggestionRow | null): Observable<PlaceAddress | null> {
        this._latest = '';
        this.clear();
        const session = this._session;
        this._session = this._places.newSession();
        return row?.id ? this._places.address(row.id, session) : of(null);
    }
}
